import React from 'react'
import LazyHydrate from 'react-lazy-hydration'
import { connect } from 'react-redux'
import { push as pushReactRouter } from 'connected-react-router'
import { ErrorBoundary, Propbar, Badge, AttributeSelectorDrawer, Column, BackToTop } from '@sainsburys-tech/bolt'

import { fetchProductStoreRequest } from '../actionCreators/productStore/productStoreActionCreators'

import ReviewStarRating from '../components/MadeToOrderComponents/ReviewStarRating/ReviewStarRating'
import Name from '../components/Name/Name'
import MediaGallery from '../components/MediaGallery'
import AttributeSelector from '../components/MadeToOrderComponents/AttributeSelector/AttributeSelector'
import Badges from '../components/Badges/Badges'
import BreadcrumbSection from './sections/BreadcrumbSection'
import useAttributeSelectorDrawer from '../hooks/useAttributeSelectorDrawer'
import Dimension from '../components/MadeToOrderComponents/Dimension/Dimension'
import PricingModuleSection from './sections/PricingModuleSection'
import AccordionsSection from './sections/AccordionsSection'
import ContentSection from './sections/ContentSection'
import StructuredDataAvailabilityUpdater from '../components/StructuredDataAvailabilityUpdater/StructuredDataAvailabilityUpdater'
import { getSelectedFabricType, getSelectedFeetColour } from '../components/MadeToOrderComponents/helpers/helpers'

import * as Styled from './MadeToOrderTemplate.styles'
import HandcraftedToYourNeedsSection from '../components/MadeToOrderComponents/HandcraftedToYourNeeds/HandcraftedToYourNeeds'

const MadeToOrderTemplate = props => {
  const {
    attributes,
    breadcrumb,
    dimensions,
    BREADCRUMB,
    MEDIA_PORTRAIT,
    showMediaGalleryGrid,
    pricingModuleOptions,
    TEMPLATE_TYPE,
    WISHLIST,
    SOLD_BY_ARGOS,
    MONTHLY_PAYMENT_PLAN,
    BRAND,
    badgesOptions,
    breadcrumbOptions,
    reviewsFlagOptions,
    mediaPlayerOptions,
    variants,
    accordionsSectionOptions,
    hasCompleteTheLook,
    hasShopCollection,
    hasAlternatives,
    showAdditionalInformation,
    hasFeaturedProducts,
    descriptionOptions,
    onAccordionChange,
    push,
    fetchProduct,
    customerContent,
    trevorSummarySection
  } = props

  const { name, partNumber } = attributes
  const productDimension = dimensions?.attributes?.Dimensions || dimensions?.attributes?.size

  const initialProductVariants = variants?.attributes?.variants

  const productVariants =
    initialProductVariants?.map(variant => ({
      ...variant,
      attributes: variant?.attributes?.filter(attr => attr.type !== 'Leg colour') || []
    })) || []

  // Get the selected fabric type and feet colour for the attribute selector
  const selectedFabricAttributes = getSelectedFabricType(productVariants, partNumber)
  const selectedFeetColourAttributes = getSelectedFeetColour(productVariants, partNumber)

  // Attribute selector items
  const attributesSelectorItems = [
    {
      ...selectedFabricAttributes
    },
    {
      ...selectedFeetColourAttributes
    }
  ]
  const propositions = [
    {
      heading: '15 year frame guarantee',
      icon: 'GuaranteeIcon',
      description: 'Our made to order sofas & chairs have a 15 year frame guarantee.'
    },
    {
      heading: '2 year frame guarantee',
      description: 'Sofabeds & footstools have a 2 year frame guarantee.',
      noBorderTop: true
    },
    {
      heading: 'Assembly included',
      icon: 'AssemblyIcon',
      description: 'We do more than deliver your product. We unpack, set it up and take away the packaging.'
    }
  ]

  // Used to show the attribute selector drawer and it's content
  const { showDrawer, selectedAttributeSelectorData, onDrawerOpen, onDrawerClose } = useAttributeSelectorDrawer(
    attributesSelectorItems
  )

  // Used to fetch the product when a new variant is selected onDrawerClose
  const onVariantChange = (selectedProduct = {}, currentPartNumber = '') => {
    if (selectedProduct?.partNumber === currentPartNumber) return

    // If the chosen part number is different to the current part number, update the product
    push(`/product/${selectedProduct?.partNumber}`)
    fetchProduct({
      partNumber: selectedProduct?.partNumber
    })

    // TODO: Check if updateCnetContent is needed
    //  updateCnetContent(partNumber)
  }

  return (
    <main className='pdp-main' itemScope itemType='https://schema.org/Product'>
      {/* Component to update structured data availability after frontend availability fetch */}
      <StructuredDataAvailabilityUpdater partNumber={partNumber} />
      <ErrorBoundary>
        <LazyHydrate ssrOnly>
          <div className='prop-bar'>
            <Propbar brand={BRAND.toLocaleLowerCase()} />
          </div>
        </LazyHydrate>
      </ErrorBoundary>
      <div className='container'>
        {BREADCRUMB && (
          <LazyHydrate whenIdle>
            <BreadcrumbSection className='xs-row pdp-breadcrumbs' {...{ breadcrumbOptions }} />
          </LazyHydrate>
        )}
        <Styled.Wrapper row='xs' gutterValue={0}>
          <Styled.MediaWrapper xs={12} md={7}>
            <LazyHydrate whenIdle>
              <MediaGallery
                {...mediaPlayerOptions}
                productName={name}
                isPortrait={MEDIA_PORTRAIT}
                isProductCare={attributes?.isWarrantyItem}
                showMediaGalleryGrid={showMediaGalleryGrid}
              />
            </LazyHydrate>
          </Styled.MediaWrapper>
          <Column xs={12} md={5} mdGutterValue={1}>
            <ErrorBoundary>
              <Badges {...badgesOptions} />
              <Styled.MtoBadge>
                <Badge mto />
              </Styled.MtoBadge>
            </ErrorBoundary>
            <ErrorBoundary>
              <Name productName={name} />
            </ErrorBoundary>
            <Dimension dimension={productDimension} />
            <ErrorBoundary>
              <ReviewStarRating {...reviewsFlagOptions} />
            </ErrorBoundary>
            {attributesSelectorItems.length > 0 && (
              <ErrorBoundary>
                <Styled.AttributeSelectorWrapper>
                  {attributesSelectorItems?.map((attributeSelectorItem, index) => {
                    return (
                      <AttributeSelector
                        key={attributeSelectorItem.heading}
                        {...attributeSelectorItem}
                        onDrawerOpen={() => onDrawerOpen(index)}
                      />
                    )
                  })}
                  <AttributeSelectorDrawer
                    showDrawer={showDrawer}
                    onDrawerClose={selectedProduct => {
                      onVariantChange(selectedProduct, partNumber)
                      onDrawerClose()
                    }}
                    attributeType={selectedAttributeSelectorData?.type}
                    productVariants={productVariants}
                    partNumber={partNumber}
                    pageType='pdp'
                  />
                </Styled.AttributeSelectorWrapper>
              </ErrorBoundary>
            )}
            <LazyHydrate whenIdle>
              <ErrorBoundary>
                <section>
                  <PricingModuleSection
                    type={TEMPLATE_TYPE}
                    pricingModuleOptions={pricingModuleOptions}
                    WISHLIST={WISHLIST}
                    SOLD_BY_ARGOS={SOLD_BY_ARGOS}
                    breadcrumb={breadcrumb}
                    MONTHLY_PAYMENT_PLAN={MONTHLY_PAYMENT_PLAN}
                    showAttributeSelector={false}
                    propositions={propositions}
                    trevorSummarySection={trevorSummarySection}
                  />
                </section>
              </ErrorBoundary>
            </LazyHydrate>
          </Column>
        </Styled.Wrapper>
        {customerContent}
        <LazyHydrate whenIdle>
          <HandcraftedToYourNeedsSection />
        </LazyHydrate>
        <LazyHydrate whenIdle>
          <ContentSection
            isAccordion
            accordionOptions={{ hasBorderTop: true }}
            descriptionId='pdp-description'
            descriptionOptions={descriptionOptions}
            onAccordionChange={onAccordionChange}
            showCategoryNumber
            isMadeToOrder
          />
        </LazyHydrate>
        <LazyHydrate whenIdle>
          <AccordionsSection
            className='xs-12--none accordion-container pdp-accordions'
            isMTO
            hasMeasureBeforeYouOrderAccordion
            {...accordionsSectionOptions}
          />
          {hasCompleteTheLook}
        </LazyHydrate>
        {hasShopCollection}
        {hasAlternatives}
        {showAdditionalInformation}
        {hasFeaturedProducts}
      </div>
      <BackToTop />
    </main>
  )
}

const mapDispatchToProps = dispatch => {
  const props = {
    push: payload => dispatch(pushReactRouter(payload)),
    fetchProduct: payload => dispatch(fetchProductStoreRequest(payload))
  }
  return props
}

export default connect(null, mapDispatchToProps)(MadeToOrderTemplate)
export { MadeToOrderTemplate, mapDispatchToProps }
